import * as $ from "sanctuary-def";
export const AgencyRecord = $.RecordType({ name: $.String });
export const PaymentSourceRecord = $.RecordType({ name: $.String });

export const PAYMENT_STATUSES = {
  ACH_RETURN: "ach_return",
  CHARGEBACK_REVERSAL: "chargeback_reversal",
  CHARGEBACK: "chargeback",
  CREDITED: "credited",
  DEBITED: "debited",
  DUPLICATED: "duplicated",
  ERRORED: "errored",
  EXPIRED: "expired",
  FAILED: "failed",
  PENDING: "pending",
  REFUNDED: "refunded",
  REJECTED: "rejected",
  REJECTED_VELOCITY: "rejected_velocity",
  REVERSAL_NEEDED: "reversal_needed",
  SUCCESSFUL: "successful",
  VOIDED: "voided",
};

export const LINKED_TRANSACTION_STATUSES = [
  PAYMENT_STATUSES.ACH_RETURN,
  PAYMENT_STATUSES.CHARGEBACK,
  PAYMENT_STATUSES.CHARGEBACK_REVERSAL,
  PAYMENT_STATUSES.REFUNDED,
  PAYMENT_STATUSES.FAILED,
  PAYMENT_STATUSES.SUCCESSFUL,
  PAYMENT_STATUSES.REVERSAL_NEEDED,
  PAYMENT_STATUSES.CREDITED,
  PAYMENT_STATUSES.DEBITED,
];

export const REFUND_TYPES = {
  FULL_AMOUNT: "full_amount",
  PARTIAL_AMOUNT_FULL_LINE_ITEM: "partial_amount_full_line_item",
  PARTIAL_AMOUNT_PARTIAL_LINE_ITEM: "partial_amount_partial_line_item",
};

export const PaymentStatus = $.EnumType("RMD/PaymentStatus")(
  "https://citybase.atlassian.net/wiki/spaces/PROD/pages/680099895/Payment+Gateway+API+docs+V1.1#PaymentGatewayAPI(docs)V1.1-PATCH/payments/{payment_id}"
)(Object.values(PAYMENT_STATUSES));

export const RefundType = $.EnumType("RMD/RefundType")("")(
  Object.values(REFUND_TYPES)
);

export const RESEARCHER = "RESEARCHER";
export const AGENCY_ADMIN = "AGENCY_ADMIN";
export const CLIENT_ADMIN = "CLIENT_ADMIN";
export const SUPERVISOR = "SUPERVISOR";
export const CASHIER = "CASHIER";
// The below "CB_ADMIN" role should be the canonical - and only - CityBase admin role we see
export const CITYBASE_ADMIN = "CB_ADMIN";
// The below "CITYBASE_ADMIN" role should NOT exist because it was created to help EXTERNAL users understand the role string's meaning ("CB_ADMIN" could be unclear as far as what the "CB" stands for)
export const CB_ADMIN = "CITYBASE_ADMIN"; // CB_ADMIN and CITYBASE_ADMIN are inconsistent in Okta, so we have to handle both cases until that's resolved.
export const OKTA_CLIENT_ADMIN = "client-admin";
export const OKTA_AGENCY_ADMIN = "agency-admin";
export const NO_ROLE = "NO_ROLE";

export const ALL_REVM_ROLE_OPTIONS = [
  NO_ROLE,
  CLIENT_ADMIN,
  AGENCY_ADMIN,
  SUPERVISOR,
  RESEARCHER,
];

export const ALL_POS_ROLE_OPTIONS = [NO_ROLE, CLIENT_ADMIN, AGENCY_ADMIN, CASHIER];

export const userRolesHierarchy = {
  [NO_ROLE]: [NO_ROLE, CLIENT_ADMIN],
  [RESEARCHER]: [NO_ROLE, RESEARCHER],
  [SUPERVISOR]: [NO_ROLE, SUPERVISOR, RESEARCHER],
  [AGENCY_ADMIN]: [NO_ROLE, AGENCY_ADMIN, SUPERVISOR, RESEARCHER],
  [CLIENT_ADMIN]: [NO_ROLE, CLIENT_ADMIN, AGENCY_ADMIN, SUPERVISOR, RESEARCHER],
  [CB_ADMIN]: [
    NO_ROLE,
    CITYBASE_ADMIN,
    CLIENT_ADMIN,
    AGENCY_ADMIN,
    SUPERVISOR,
    RESEARCHER,
  ],
  [CITYBASE_ADMIN]: [
    NO_ROLE,
    CITYBASE_ADMIN,
    CLIENT_ADMIN,
    AGENCY_ADMIN,
    SUPERVISOR,
    RESEARCHER,
  ],
};

export const roleDictionary = {
  [NO_ROLE]: "No role or access",
  [CASHIER]: "Cashier",
  [RESEARCHER]: "Researcher",
  [AGENCY_ADMIN]: "Agency admin",
  [CLIENT_ADMIN]: "Client admin",
  [SUPERVISOR]: "Supervisor",
  [CB_ADMIN]: "Citybase admin",
  [CITYBASE_ADMIN]: "CityBase admin",
};

export const UserRole = $.EnumType("RMD/UserRole")(
  "https://docs.google.com/spreadsheets/d/1TpB1ee60cBoX4VIwoOt5QzP7TBKM9El1tK2OiJg1GH8/edit#gid=0"
)([
  NO_ROLE,
  CASHIER,
  RESEARCHER,
  SUPERVISOR,
  AGENCY_ADMIN,
  CLIENT_ADMIN,
  CB_ADMIN,
  CITYBASE_ADMIN,
]);

export const ClientRecord = $.RecordType({
  brand: $.String,
});

export const ProfileRecord = $.RecordType({
  first_name: $.String,
  last_name: $.String,
  email: $.String,
});

export const ClientSlugRecord = $.RecordType({
  slug: $.String,
});

export const CredentialsProviderRecord = $.RecordType({
  type: $.String,
});

export const AgencySlugRecord = $.RecordType({
  slug: $.String,
});

export const AccessRecord = $.RecordType({
  platform: $.String,
  role: $.String,
});

export const SettingsPointOfSaleRecord = $.RecordType({
  enabled: $.Boolean,
});

// If new Revenue Management Roles are added, we will need to account
// for them here as keys in the response (TO DO: Improve this type)
export const RoleCombinationRecord = $.RecordType({
  role_combinations: $.RecordType({
    no_role: $.Array($.String),
    supervisor: $.Array($.String),
    researcher: $.Array($.String),
    agency_admin: $.Array($.String),
    client_admin: $.Array($.String),
    cb_admin: $.Array($.String),
  }),
});

export const REVENUE_MANAGEMENT = "revenue-management";
export const POINT_OF_SALE = "cb-pos";
export const ACTIVE = "ACTIVE";
export const DEACTIVATED = "DEACTIVATED";
export const Status = $.EnumType("RMD/Status")(
  "https://docs.google.com/spreadsheets/d/15KCF6cs6JlW8dKnlCADk50igiB2KQ5_bniGDCFzyH60/edit#gid=0"
)([ACTIVE, DEACTIVATED]);

export const FEDERATION = "FEDERATION";
export const OKTA = "OKTA";
export const CredentialsProvider = $.EnumType("RMD/CredentialsProvider")(
  "https://docs.google.com/spreadsheets/d/15KCF6cs6JlW8dKnlCADk50igiB2KQ5_bniGDCFzyH60/edit#gid=0"
)([FEDERATION, OKTA]);

//agency constants
export const SELECT_ALL = "Select All";
export const SELECT_ALL_SLUG = "all";

// https://help.okta.com/oie/en-us/content/topics/users-groups-profiles/usgp-end-user-states.htm
export const OKTA_STATUSES = {
  STAGED: "STAGED",
  PROVISIONED: "PROVISIONED",
  ACTIVE: "ACTIVE",
  RECOVERY: "RECOVERY",
  PASSWORD_EXPIRED: "PASSWORD_EXPIRED",
  LOCKED_OUT: "LOCKED_OUT",
  SUSPENDED: "SUSPENDED",
  DEPROVISIONED: "DEPROVISIONED",
};
